import './playlists.professional-caregiver.screen.scss';
import {Box, Stack} from '@mui/material';
import {MutableRefObject, useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams, useRouteMatch} from 'react-router-dom';
import {PlaylistType} from '../../../../../../backend/enums/playlist-type';
import {Playlist} from '../../../../../../backend/interfaces/playlist';
import {PaginatedTable} from '../../../../../../components/paginated-table/paginated-table';
import {usePaginatedTable} from '../../../../../../hooks/paginated-table';
import {translations} from '../../../../../../i18n/translations';
import {PaginatedTableData} from '../../../../../../interfaces/paginated-table-data';
import {PlaylistsRepository} from '../../../../../../repositories/playlists';
import {AuthContext} from '../../../../../../contexts/auth/context';
import {AuthContextType} from '../../../../../../contexts/auth/types';
import {SnackBarContext} from '../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../contexts/snack-bar/types';
import {AlertModal} from '../../../../../../components/alert-modal/alert-modal';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import SearchInput from '../../../../../../components/search-input/search-input';
import {ScreenPaths} from '../../../../../../enums';
import {PlaylistsMenu} from '../../../../../../components/user-playlists/playlists-menu';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  State = 'state',
}

export default function PlaylistsProfessionalCaregiverOfficeScreen({
  canWrite,
}: {
  canWrite: boolean;
}) {
  // Get params
  let {entityId}: {entityId?: string} = useParams();

  // Get context
  const authContext: AuthContextType = useContext(AuthContext);
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);

  // Hold state
  const [playlistType, setPlaylistType] = useState<PlaylistType>(PlaylistType.Karaoke);
  const [updatePlaylistDialogOpened, setUpdatePlaylistDialogOpened] = useState<{
    disableDialogOpened: boolean;
    enableDialogOpened: boolean;
  }>({disableDialogOpened: false, enableDialogOpened: false});

  // Hold ref
  const playlistTypeRef: MutableRefObject<PlaylistType> = useRef(PlaylistType.Karaoke);
  const dataToUpdateRef: MutableRefObject<
    {playlistId: string; playlistName: string; playlistActiveFlag: boolean} | undefined
  > = useRef();

  // Translations
  const {t} = useTranslation();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();

  // Fetch playlists
  const handleGetPlaylists = async (limit: number, skip: number, search?: string) => {
    // Check entity id
    if (!entityId) {
      // Error
      throw new Error('Empty entity id.');
    }

    // Load playlists
    const result: {totalCount: number; data: Array<Playlist>} =
      await PlaylistsRepository.instance.getPlaylistsInEntityAsAffiliatedForEntity(
        entityId,
        skip,
        limit,
        playlistTypeRef.current,
        true,
        search
      );

    // Process data
    const processedData: PaginatedTableData<Playlist> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        enabled: obj.isActive,
        avatarUrl: obj.imageUrl,
        toLink: url.charAt(url.length - 1) === '/' ? `${url}${obj.id}` : `${url}/${obj.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            // The name is localized
            value: obj.name || '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.isActive
              ? t(translations.playlists.professional_caregiver.root.state_value_true)
              : t(translations.playlists.professional_caregiver.root.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateItem,
    handleUpdateCurrentSearch,
  } = usePaginatedTable<Playlist>({
    fetchData: handleGetPlaylists,
  });

  // Change the playlist type
  const handleChangePlaylistType = (value: string) => {
    if (value !== playlistTypeRef.current) {
      // Update the playlist type
      setPlaylistType(value as PlaylistType);
    }
  };

  // Fetch the playlists again when the type changes
  useEffect(() => {
    if (playlistType !== playlistTypeRef.current) {
      // Update the playlist type
      playlistTypeRef.current = playlistType;

      // Fetch again
      handleApplyFilters(1);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistType]);

  const handleAskUpdatePlaylistState = (playlistId: string, value: boolean) => {
    // Get playlist name
    const playlistName: string | undefined = data
      .find((obj) => obj.id === playlistId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Name)?.value;

    // Set playlist info for later
    dataToUpdateRef.current = {
      playlistId: playlistId,
      playlistActiveFlag: value,
      playlistName: playlistName || '',
    };

    // Open modal
    setUpdatePlaylistDialogOpened({
      disableDialogOpened: !value,
      enableDialogOpened: value,
    });
  };

  const handleDeclineUpdatePlaylistState = () => {
    // Close modal
    setUpdatePlaylistDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Clear
    dataToUpdateRef.current = undefined;
  };

  const handleConfirmUpdatePlaylistState = () => {
    // Close modal
    setUpdatePlaylistDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Update
    handleUpdatePlaylistState();
  };

  // Enable/disable playlist
  const handleUpdatePlaylistState = () => {
    if (dataToUpdateRef.current) {
      // Keep values
      const playlistId = dataToUpdateRef.current.playlistId;
      const value = dataToUpdateRef.current.playlistActiveFlag;

      // Clear
      dataToUpdateRef.current = undefined;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === playlistId)?.item;
      if (updatedItem) {
        updatedItem.isActive = value;
      }

      // Update item in UI
      handleUpdateItem(
        playlistId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: value
              ? t(translations.playlists.professional_caregiver.root.state_value_true)
              : t(translations.playlists.professional_caregiver.root.state_value_false),
          },
        ],
        updatedItem,
        value
      );

      // Make request to the backend
      // Note: Don't await because it can take some time to update
      continueUpdatePlaylistState(entityId || '', playlistId, value);
    } else {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.playlists.professional_caregiver.root.errors
            .update_state_playlist_error_message
        ),
      });

      // Not expected. Log.
      console.debug(
        '🔴 Could not update the state of the playlist. Information was missing to perform the request.'
      );
    }
  };

  const continueUpdatePlaylistState = async (
    entityId: string,
    playlistId: string,
    value: boolean
  ) => {
    try {
      // Make request to the backend
      await PlaylistsRepository.instance.updatePlaylistActiveStateForAllUsersAsAffiliatedForEntity(
        entityId,
        playlistId,
        value
      );
    } catch (error) {
      // Old flag
      const oldFlag: boolean = !value;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === playlistId)?.item;
      if (updatedItem) {
        updatedItem.isActive = oldFlag;
      }

      // Change item back
      handleUpdateItem(
        playlistId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: oldFlag
              ? t(translations.playlists.professional_caregiver.root.state_value_true)
              : t(translations.playlists.professional_caregiver.root.state_value_false),
          },
        ],
        updatedItem,
        oldFlag
      );

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: value
          ? t(
              translations.playlists.professional_caregiver.root.errors
                .enable_playlist_error_message
            )
          : t(
              translations.playlists.professional_caregiver.root.errors
                .disable_playlist_error_message
            ),
      });

      // Log
      console.debug(
        `🔴 Could not update the state of the playlist with id ${playlistId} to ${value}.`,
        error
      );
    }
  };

  const baseUrl = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}`;

  return (
    <>
      <GenericHeader
        isLoading={false}
        titleCrumbs={[
          {
            label: t(translations.office.professional_caregiver.tabs.digital_inclusion),
            link: baseUrl,
          },
          {
            label: t(translations.office.professional_caregiver.tabs.playlists),
            link: url,
          },
        ]}
        leftColumn={
          <Stack flexDirection="row" gap="20px">
            <SearchInput
              placeholder={t(
                translations.playlists.professional_caregiver.root.search_input_placeholder
              )}
              value={currentSearch}
              onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
              onSearchAction={(value: string) => handleApplyFilters(1, value)}
            />
            <PlaylistsMenu
              playlistType={playlistType}
              onChangePlaylistType={handleChangePlaylistType}
            />
          </Stack>
        }
        rightColumn={<></>}
      />

      <Box className="listing-container">
        <Box className="listing-container-centered">
          <PaginatedTable
            avatar={{
              type: 'other',
            }}
            emptyTableMessage={t(translations.playlists.professional_caregiver.root.no_playlists)}
            columns={[
              {
                columnId: ColumnIdentifier.Name,
                title: t(translations.playlists.professional_caregiver.root.name_header),
                flexGrow: 2,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.playlists.professional_caregiver.root.state_header),
                flexGrow: 1,
                button: {
                  type: 'toggle',
                  showColumnValue: true,
                  toggle: {
                    enabledValue: t(
                      translations.playlists.professional_caregiver.root.state_value_true
                    ),
                    handleClick: handleAskUpdatePlaylistState,
                  },
                  disabled: !canWrite,
                  disabledTooltip: t(translations.user_playlists.state_disabled_tooltip),
                },
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={true}
          />
        </Box>
      </Box>

      <AlertModal
        id="confirm-disable-playlist-state-entity-users"
        opened={updatePlaylistDialogOpened.disableDialogOpened}
        title={t(translations.playlists.professional_caregiver.root.disable_playlist_dialog_title, {
          name: dataToUpdateRef.current?.playlistName || '',
        })}
        content={t(
          translations.playlists.professional_caregiver.root.disable_playlist_dialog_message,
          {
            name: authContext.state.session?.selectedAffiliation?.entity.name || '',
          }
        )}
        handleClose={handleDeclineUpdatePlaylistState}
        actions={[
          {
            label: t(
              translations.playlists.professional_caregiver.root
                .disable_playlist_dialog_action_yes_button
            ),
            type: 'destructive',
            handleClick: handleConfirmUpdatePlaylistState,
          },
          {
            label: t(
              translations.playlists.professional_caregiver.root
                .disable_playlist_dialog_action_no_button
            ),
            type: 'neutral',
            handleClick: handleDeclineUpdatePlaylistState,
          },
        ]}
      />
      <AlertModal
        id="confirm-enable-playlist-state-entity-users"
        opened={updatePlaylistDialogOpened.enableDialogOpened}
        title={t(translations.playlists.professional_caregiver.root.enable_playlist_dialog_title, {
          name: dataToUpdateRef.current?.playlistName || '',
        })}
        content={t(
          translations.playlists.professional_caregiver.root.enable_playlist_dialog_message,
          {
            name: authContext.state.session?.selectedAffiliation?.entity.name || '',
          }
        )}
        handleClose={handleDeclineUpdatePlaylistState}
        actions={[
          {
            label: t(
              translations.playlists.professional_caregiver.root
                .enable_playlist_dialog_action_no_button
            ),
            type: 'neutral',
            handleClick: handleDeclineUpdatePlaylistState,
          },
          {
            label: t(
              translations.playlists.professional_caregiver.root
                .enable_playlist_dialog_action_yes_button
            ),
            type: 'nondestructive',
            handleClick: handleConfirmUpdatePlaylistState,
          },
        ]}
      />
    </>
  );
}
